/**
 * Vanilla JS Dropdown Manager
 * Replacement for Bootstrap dropdown functionality
 * 
 * Usage:
 *   <button data-dropdown-toggle="myDropdown">Toggle</button>
 *   <div id="myDropdown" class="hidden">...</div>
 */

var DropdownManager = (function() {
  'use strict';

  var activeDropdown = null;

  /**
   * Toggle dropdown visibility
   */
  function toggle(dropdownId, triggerElement) {
    var dropdown = document.getElementById(dropdownId);
    if (!dropdown) {
      console.error('Dropdown not found:', dropdownId);
      return;
    }

    var isHidden = dropdown.classList.contains('hidden');
    
    // Close all other dropdowns first
    closeAll();

    if (isHidden) {
      open(dropdownId, triggerElement);
    }
  }

  /**
   * Open a dropdown
   */
  function open(dropdownId, triggerElement) {
    var dropdown = document.getElementById(dropdownId);
    if (!dropdown) return;

    dropdown.classList.remove('hidden');
    dropdown.classList.add('block');
    activeDropdown = {
      id: dropdownId,
      element: dropdown,
      trigger: triggerElement
    };

    // Update trigger aria-expanded
    if (triggerElement) {
      triggerElement.setAttribute('aria-expanded', 'true');
    }

    // Position dropdown
    positionDropdown(dropdown, triggerElement);

    // Emit custom event
    var event = new CustomEvent('dropdown:opened', { detail: { dropdownId: dropdownId } });
    document.dispatchEvent(event);
  }

  /**
   * Close a specific dropdown
   */
  function close(dropdownId) {
    var dropdown = document.getElementById(dropdownId);
    if (!dropdown) return;

    dropdown.classList.remove('block');
    dropdown.classList.add('hidden');

    // Update trigger aria-expanded
    if (activeDropdown && activeDropdown.trigger) {
      activeDropdown.trigger.setAttribute('aria-expanded', 'false');
    }

    if (activeDropdown && activeDropdown.id === dropdownId) {
      activeDropdown = null;
    }

    // Emit custom event
    var event = new CustomEvent('dropdown:closed', { detail: { dropdownId: dropdownId } });
    document.dispatchEvent(event);
  }

  /**
   * Close all dropdowns
   */
  function closeAll() {
    if (activeDropdown) {
      close(activeDropdown.id);
    }
  }

  /**
   * Position dropdown relative to trigger
   */
  function positionDropdown(dropdown, trigger) {
    if (!trigger) return;

    var triggerRect = trigger.getBoundingClientRect();
    var offsetParent = dropdown.offsetParent || trigger.offsetParent || trigger.parentElement;
    if (!offsetParent) return;

    var parentRect = offsetParent.getBoundingClientRect();
    var dropdownRect = dropdown.getBoundingClientRect();

    dropdown.style.position = 'absolute';
    dropdown.style.top = (triggerRect.bottom - parentRect.top) + 'px';
    dropdown.style.left = (triggerRect.left - parentRect.left) + 'px';
    dropdown.style.right = 'auto';

    if ((triggerRect.left - parentRect.left) + dropdownRect.width > parentRect.width) {
      dropdown.style.left = 'auto';
      dropdown.style.right = Math.max(parentRect.right - triggerRect.right, 0) + 'px';
    }
  }

  /**
   * Handle clicks outside dropdown
   */
  function handleClickOutside(e) {
    if (!activeDropdown) return;

    var clickedInside = activeDropdown.element.contains(e.target) || 
                        (activeDropdown.trigger && activeDropdown.trigger.contains(e.target));
    
    if (!clickedInside) {
      close(activeDropdown.id);
    }
  }

  /**
   * Handle ESC key
   */
  function handleEscape(e) {
    if (e.key === 'Escape' && activeDropdown) {
      close(activeDropdown.id);
      if (activeDropdown.trigger) {
        activeDropdown.trigger.focus();
      }
    }
  }

  /**
   * Initialize all dropdown triggers
   */
  function init() {
    // Add global click listener
    document.addEventListener('click', handleClickOutside);
    
    // Add ESC key listener
    document.addEventListener('keydown', handleEscape);

    // Find all dropdown toggles
    var toggles = document.querySelectorAll('[data-dropdown-toggle]');
    toggles.forEach(function(toggleElement) {
      toggleElement.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var dropdownId = this.getAttribute('data-dropdown-toggle');
        toggle(dropdownId, this);
      });
    });
  }

  // Public API
  return {
    init: init,
    toggle: toggle,
    open: open,
    close: close,
    closeAll: closeAll
  };
})();

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', DropdownManager.init);
} else {
  DropdownManager.init();
}

window.DropdownManager = DropdownManager;
